import type {
  ImagingDataMock,
  ImagingOrderIncludedImagingResult,
  ImagingOrderIncludedOrder,
  ImagingOrderIncludedResource,
  ImagingOrderResultCreatePayload
} from '@type/imaging_order.type'
import type { ImagingResultAttachmentIncluded } from '@type/imaging_results.type'
import type { OrderImagingIncluded, OrderIncluded } from '@type/order.type'
import { asPerformedAt, type PerformedAt } from '@util/testCatalogue'
import moment from 'moment'

export type ImagingPlacedLine = {
  key: string
  label: string
  orderId: string
  lineStatus: string
  /** `outside` studies are referred out: no bill, not in the department queue, resulted by upload. */
  performedAt: PerformedAt
}

/** Lines done at an outside facility (what the referral slip lists and what can be uploaded for). */
export const outsideImagingLines = (lines: ImagingPlacedLine[]) =>
  lines.filter(
    line => line.performedAt === 'outside' && !['cancelled', 'canceled'].includes(line.lineStatus.toLowerCase())
  )

export function getImagingIncluded(
  included: (ImagingOrderIncludedResource | OrderIncluded)[] | undefined,
  id: string | undefined
): OrderImagingIncluded | undefined {
  return included?.find((item): item is OrderImagingIncluded => item?.id === id && item.type === 'imaging')
}

export function isImagingResultIncluded(
  item: ImagingOrderIncludedResource | OrderIncluded | { type: string }
): item is ImagingOrderIncludedImagingResult {
  return item.type === 'imaging_result'
}

function catalogLineLabel(
  child: ImagingOrderIncludedOrder | undefined,
  included: (ImagingOrderIncludedResource | OrderIncluded)[] | undefined,
  index: number
): { label: string; fromImaging: boolean } {
  const orderable = child?.relationships?.orderable
  const imagingIdFromRel =
    orderable && typeof orderable === 'object' && 'data' in orderable
      ? (orderable.data as { id?: string } | null | undefined)?.id
      : undefined
  const imagingId =
    imagingIdFromRel != null
      ? String(imagingIdFromRel)
      : child?.attributes.orderable_id != null
        ? String(child.attributes.orderable_id)
        : undefined
  const imaging = imagingId
    ? included?.find((x): x is OrderImagingIncluded => x.type === 'imaging' && String(x.id) === imagingId)
    : undefined
  const fromImaging = !!(imaging?.attributes.study_type?.trim() || child?.attributes.name?.trim())
  const label = imaging?.attributes.study_type?.trim() || child?.attributes.name?.trim() || `Study ${index + 1}`
  return { fromImaging, label }
}

/**
 * One line per study.
 *
 * Catalog lines come from `relationships.orders` (one child `order` per imaging_id).
 * Free-text lines come from `attributes.study_names` after removing catalog labels.
 * When list `included` omits `imaging` rows, catalog labels fall back by consuming a
 * `study_names` entry (API mirrors catalog names there) instead of inventing "Study N".
 */
export function buildImagingPlacedLines(
  data: ImagingDataMock | undefined,
  included: (ImagingOrderIncludedResource | OrderIncluded)[] | undefined
): ImagingPlacedLine[] {
  if (!data) return []

  if (data.type === 'imaging_order') {
    const orderRefs = data.relationships.orders?.data ?? []
    const remainingNames = (data.attributes.study_names ?? [])
      .map(name => name?.trim())
      .filter((label): label is string => !!label)
    const outsideNames = (data.attributes.outside_names ?? []).map(name => name.trim().toLowerCase())

    const catalogLines: ImagingPlacedLine[] = orderRefs.map((ref, i) => {
      const child = included?.find((x): x is ImagingOrderIncludedOrder => x.type === 'order' && x.id === ref.id)
      const resolved = catalogLineLabel(child, included, i)
      let label = resolved.label

      if (resolved.fromImaging) {
        const idx = remainingNames.findIndex(name => name.toLowerCase() === label.trim().toLowerCase())
        if (idx >= 0) remainingNames.splice(idx, 1)
      } else {
        // No imaging sideload — consume next study_name as this catalog line's label.
        const consumed = remainingNames.shift()
        if (consumed) label = consumed
      }

      return {
        key: ref.id,
        label,
        lineStatus: String(child?.meta?.status ?? data.attributes.status ?? 'pending'),
        orderId: ref.id,
        performedAt: asPerformedAt(child?.attributes.performed_at)
      }
    })

    const freeTextLines: ImagingPlacedLine[] = remainingNames.map((label, i) => ({
      key: `study-name-${i}-${label}`,
      label,
      lineStatus: String(data.attributes.status ?? 'pending'),
      orderId: '',
      performedAt: outsideNames.includes(label.toLowerCase()) ? 'outside' : 'our_lab'
    }))

    return [...catalogLines, ...freeTextLines]
  }

  const imaging = getImagingIncluded(included, data.relationships.orderable.data?.id)
  return [
    {
      key: data.id,
      label: imaging?.attributes.study_type ?? data.attributes.name ?? 'Imaging test',
      lineStatus: String(data.meta?.status ?? 'pending'),
      orderId: data.id,
      performedAt: 'our_lab'
    }
  ]
}

function relationDataId(rel: unknown): string | undefined {
  if (!rel || typeof rel !== 'object' || !('data' in rel)) return undefined
  const data = (rel as { data?: { id?: string | number } | null }).data
  return data?.id != null ? String(data.id) : undefined
}

/** Child catalog `order` id bound on an imaging_result, if present. */
export function getResultBoundOrderId(result: ImagingOrderIncludedImagingResult): string | undefined {
  return relationDataId(result.relationships?.order)
}

export function withResultOrderRelationship(
  result: ImagingOrderIncludedImagingResult,
  orderId: string
): ImagingOrderIncludedImagingResult {
  if (getResultBoundOrderId(result) === orderId) return result
  return {
    ...result,
    relationships: {
      ...result.relationships,
      order: { data: { type: 'order', id: orderId } }
    }
  }
}

export function toIncludedImagingResult(row: {
  id: string
  type: string
  attributes: ImagingOrderIncludedImagingResult['attributes']
  relationships?: ImagingOrderIncludedImagingResult['relationships']
}): ImagingOrderIncludedImagingResult {
  return {
    id: row.id,
    type: 'imaging_result',
    attributes: row.attributes ?? {},
    relationships: row.relationships
  }
}

/**
 * Imaging results sideloaded on the parent imaging_order (and relationship refs when present).
 * Does not fetch catalog child-order results — use `useBundleImagingResults` for that.
 */
export function imagingResultsFromBundleIncluded(
  data: ImagingDataMock | undefined,
  included: (ImagingOrderIncludedResource | OrderIncluded)[] | undefined
): ImagingOrderIncludedImagingResult[] {
  if (!included?.length) return []

  const byId = new Map<string, ImagingOrderIncludedImagingResult>()
  for (const item of included) {
    if (isImagingResultIncluded(item)) byId.set(String(item.id), item)
  }

  if (data?.type === 'imaging_order') {
    const rel = data.relationships.imaging_results
    if (rel && typeof rel === 'object' && 'data' in rel && Array.isArray(rel.data)) {
      const ordered: ImagingOrderIncludedImagingResult[] = []
      const seen = new Set<string>()
      for (const ref of rel.data) {
        const found = byId.get(String(ref.id))
        if (found && !seen.has(found.id)) {
          seen.add(found.id)
          ordered.push(found)
        }
      }
      for (const result of Array.from(byId.values())) {
        if (!seen.has(result.id)) ordered.push(result)
      }
      return ordered
    }
  }

  return Array.from(byId.values())
}

/** Catalog child order ids that still lack a result bound via `relationships.order`. */
export function catalogOrderIdsMissingResult(
  data: ImagingDataMock | undefined,
  results: ImagingOrderIncludedImagingResult[]
): string[] {
  if (!data || data.type !== 'imaging_order') return []
  const bound = new Set(results.map(getResultBoundOrderId).filter((id): id is string => !!id))
  return (data.relationships.orders?.data ?? []).map(ref => ref.id).filter(id => !bound.has(id))
}

/** Result ids already linked on sideloaded child `order` rows (when relationship data is present). */
export function catalogResultRefsFromIncludedOrders(
  data: ImagingDataMock | undefined,
  included: (ImagingOrderIncludedResource | OrderIncluded)[] | undefined
): { orderId: string; resultId: string }[] {
  if (!data || data.type !== 'imaging_order' || !included?.length) return []
  const out: { orderId: string; resultId: string }[] = []
  for (const ref of data.relationships.orders?.data ?? []) {
    const child = included.find((x): x is ImagingOrderIncludedOrder => x.type === 'order' && x.id === ref.id)
    const resultId = relationDataId(child?.relationships?.imaging_result)
    if (resultId) out.push({ orderId: ref.id, resultId })
  }
  return out
}

export function orderTitleNumber(rawId: string) {
  const n = Number.parseInt(rawId, 10)
  if (!Number.isNaN(n)) return String(n).padStart(7, '0')
  return rawId
}

/**
 * `POST /imaging_orders/:id/imaging_results` body.
 *
 * `locked_at` is deliberately omitted: the order moves to `in_progress` and the doctor
 * presses Complete, rather than the upload silently completing the order.
 */
export function createImagingResultAttachmentPayload(
  signed_id_or_ids: string | string[],
  study_name: string
): ImagingOrderResultCreatePayload {
  const attachment_signed_ids = Array.isArray(signed_id_or_ids) ? signed_id_or_ids : [signed_id_or_ids]
  return {
    data: {
      type: 'imaging_result',
      attributes: {
        attachment_signed_ids,
        study_name
      }
    }
  }
}

/** `POST /orders/:order_id/imaging_results` attachment body (catalog child line). */
export function createCatalogImagingResultAttachmentPayload(signed_id_or_ids: string | string[]) {
  const attachment_signed_ids = Array.isArray(signed_id_or_ids) ? signed_id_or_ids : [signed_id_or_ids]
  return {
    data: {
      type: 'imaging_result' as const,
      attributes: {
        attachment_signed_ids
      }
    }
  }
}

/** `PATCH /imaging_results/:id` — attach file(s) to an existing result. */
export function createImagingResultAttachmentUpdatePayload(resultId: string, signed_id_or_ids: string | string[]) {
  const attachment_signed_ids = Array.isArray(signed_id_or_ids) ? signed_id_or_ids : [signed_id_or_ids]
  return {
    data: {
      type: 'imaging_result' as const,
      id: resultId,
      attributes: {
        attachment_signed_ids
      }
    }
  }
}

/** `PATCH /imaging_results/:id` — remove one attachment from S3 / the result. */
export function createImagingResultPurgeAttachmentPayload(resultId: string, attachmentId: number | string) {
  return {
    data: {
      type: 'imaging_result' as const,
      id: resultId,
      attributes: {
        purge_attachment_id: Number(attachmentId)
      }
    }
  }
}

export function formatAttachmentSize(byteSize?: number) {
  if (!byteSize) return '0.00 MB'
  return `${(byteSize / (1024 * 1024)).toFixed(2)} MB`
}

/** Label for a bundle `imaging_result` row: filename → timestamp → `#id`, suffixed with its study. */
export function imagingResultRowLabel(result: ImagingOrderIncludedImagingResult) {
  const base = result.attributes.attachment?.filename
    ? result.attributes.attachment.filename
    : result.attributes.created_at
      ? `Imaging result · ${moment(result.attributes.created_at).format('MMM D, YYYY h:mm A')}`
      : `Imaging result · #${result.id}`
  const studyName = result.attributes.study_name?.trim()
  return studyName ? `${base} (${studyName})` : base
}

/** Label for a legacy `meta.result_attachments` entry. */
export function imagingResultListLabel(file: ImagingResultAttachmentIncluded, fallbackIndex: number) {
  return file.filename || `Imaging result ${fallbackIndex + 1}`
}
